import { ConnectedPosition, Overlay, PositionStrategy } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  InputSignal,
  OnInit,
  OutputRef,
  signal,
  Signal,
  untracked,
  viewChildren,
  WritableSignal,
} from '@angular/core';
import { outputFromObservable, takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCard, MatCardActions, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { ButtonComponent, IconButtonComponent, OverlayDirective } from '@libs/common/components';
import { InputItemModel, SubmitGroupItemModel, SubmitItem } from '@libs/form/models';
import { ViewportService } from '@libs/shared/services';
import { ScrollIndicatorDirective } from '@libs/shared/ui';
import { filter, Observable, Subscription, switchMap, tap } from 'rxjs';
import { DynamicInputItemDirective } from '../../../directives';
import { DYNAMIC_INPUT_ITEMS_MAP } from '../../../models';
import { SubmitGroupComponent } from '../../actions';
import { InputItemsMap } from '../../input';
import { CHIP_CONTROL_SUBMIT_CLOSE, CHIP_CONTROL_SUBMIT_CONFIG, CHIP_CONTROL_SUBMIT_RESET } from './api';
import { ChipControlComponent } from './chip-control';

@Component({
  selector: 'ui-form-chip-control-group',
  templateUrl: './chip-control-group.component.html',
  styleUrls: ['./chip-control-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: DYNAMIC_INPUT_ITEMS_MAP, useValue: InputItemsMap },
  ],
  hostDirectives: [
    ScrollIndicatorDirective,
  ],
  imports: [
    ButtonComponent,
    ChipControlComponent,
    OverlayDirective,
    FormsModule,
    ReactiveFormsModule,
    SubmitGroupComponent,
    MatCard,
    MatCardTitle,
    MatCardActions,
    MatCardContent,
    MatCardHeader,
    IconButtonComponent,
    DynamicInputItemDirective,
  ],
})
export class ChipControlGroupComponent implements OnInit {
  private readonly injector: Injector = inject(Injector);
  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);

  public inputItems: InputSignal<InputItemModel[]> = input.required();

  public groupLabel: Signal<string> = input('Alle Filter');
  public showResetAll: InputSignal<boolean> = input(true);
  public resetAllLabel: InputSignal<string> = input('Zurücksetzen');

  public chips: Signal<readonly ChipControlComponent[]> = viewChildren(ChipControlComponent);
  public activeChipCount: WritableSignal<number> = signal(0);

  public overlayFormClass: InputSignal<string | undefined> = input();

  public isMobile: Signal<boolean> = toSignal(inject(ViewportService).isMobile(), { initialValue: false });

  public readonly submitConfig: SubmitGroupItemModel = CHIP_CONTROL_SUBMIT_CONFIG;

  public formItems: WritableSignal<InputItemModel[]> = signal([]);
  public formGroup: WritableSignal<FormGroup> = signal(new FormGroup({}));
  public selectedItem: WritableSignal<InputItemModel | undefined> = signal(undefined);

  public formTitle: Signal<string> = computed(() => this.selectedItem()?.label ?? this.groupLabel());

  public readonly inputItems$: Observable<InputItemModel[]> = toObservable(this.inputItems);
  public readonly selectedItem$: Observable<InputItemModel | undefined> = toObservable(this.selectedItem);

  public readonly formUpdateSubscription: Subscription = this.inputItems$
    .pipe(
      takeUntilDestroyed(),
      tap((items) => {
        const selected: InputItemModel | undefined = this.selectedItem();

        // Clear form items and update view
        // This prevents propagation of new FormGroup values to old (not yet updated) form items
        this.formItems.set([]);
        this.changeDetector.detectChanges();

        this.formGroup.set(this.buildFormGroup(this.inputItems()));
        this.selectedItem.set(
          selected ? items.find(item => item.name === selected.name) : undefined,
        );
      }),
      switchMap(items => this.selectedItem$.pipe(
        tap(selectedItem => {
          this.formItems.set(this.buildFormItems(items, selectedItem));
        }),
      )),
    )
    .subscribe();

  public readonly valueChange: OutputRef<unknown> = outputFromObservable(
    toObservable(this.formGroup)
      /* istanbul ignore next */
      .pipe(
        switchMap(form => form.valueChanges.pipe(
          filter(() => {
            return form.valid;
          }),
        )),
      ),
  );

  public readonly overlayMobilePositionStrategy: PositionStrategy = inject(Overlay).position().global().top('0px').left('0px');
  public readonly flexibleConnectedPositionDesktop: ConnectedPosition = {
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
  };

  public ngOnInit(): void {
    effect(() => {
      const chipCount: number = this.chips().filter(x => x.isActive()).length;

      untracked(() => {
        this.activeChipCount.set(chipCount);
      });
    }, { injector: this.injector });
  }

  public onSubmit(item: SubmitItem, overlay: OverlayDirective): void {
    switch (item.name) {
      case CHIP_CONTROL_SUBMIT_RESET:
        this.resetControls(this.formItems());

        break;
      case CHIP_CONTROL_SUBMIT_CLOSE:
        overlay.close();

        break;
    }
  }

  public resetControls(inputItems: InputItemModel[]): void {
    const groupValue: Record<string, unknown> = this.formGroup().value as Record<string, unknown>;

    for (const item of inputItems) {
      groupValue[item.name] = item.defaultValue;
    }

    this.formGroup().setValue(groupValue, { emitEvent: true });
  }

  private buildFormGroup(inputItems: InputItemModel[]): FormGroup {
    const group: FormGroup = new FormGroup({});

    inputItems.forEach(item => {
      group.addControl(item.name, new FormControl(item.value));
    });

    return group;
  }

  private buildFormItems(inputItems: InputItemModel[], selectedItem?: InputItemModel): InputItemModel[] {
    return selectedItem
      ? [{ ...selectedItem, label: '' }]
      : inputItems.filter(x => !x.attributes?.disabled);
  }
}
